import React, { useState, useEffect } from 'react';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Eye,
  Pencil,
  ShieldCheck,
  Plus,
} from 'lucide-react';
import Loader from '../../../../common/Loader/Loader';
import { Toaster } from '../../../../common/Toaster/Toaster';
import { useHrReferenceData } from '../../core/utils/useHrReferenceData';

const TH = 'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700';
const TH_R = 'px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-700';
const TD = 'px-3 py-2.5 text-sm text-gray-700 align-middle';
const TD_STRONG = 'px-3 py-2.5 text-sm font-medium text-gray-900 align-middle';
const TD_ACT = 'px-3 py-2.5 text-sm text-gray-800 align-middle whitespace-nowrap text-right';
const TR = 'hover:bg-[#FFF3EB]/50';
const TR_ADD = 'bg-orange-50/50 border-t-2 border-dashed border-orange-200/90';
const INPUT = 'w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-900 focus:border-orange-primary focus:outline-none focus:ring-1 focus:ring-orange-primary/30';
const BTN_EDIT = 'shrink-0 rounded-lg border border-orange-200 bg-white px-3 py-1.5 text-sm font-medium text-orange-primary transition hover:bg-orange-50';
const BTN_GHOST = 'text-sm font-medium text-gray-500 hover:text-gray-800';
const BTN_DANGER = 'text-sm font-medium text-red-600 hover:text-red-700';
const BTN_ADD = 'inline-flex items-center gap-1 rounded-lg border border-orange-200 bg-orange-50/80 px-4 py-2 text-sm font-medium text-orange-primary hover:bg-orange-100 disabled:opacity-40 disabled:pointer-events-none';

export default function HrReferenceDataPage() {
  const { departmentsWithRoles, loading, saving, toast, saveDepartmentRow, saveRoleRow } = useHrReferenceData();
  const [pageMode, setPageMode] = useState('view');
  const [openSections, setOpenSections] = useState({ departments: true, roles: true });
  const [editRow, setEditRow] = useState(null);
  const [deptForm, setDeptForm] = useState({ title: '', description: '' });
  const [roleForm, setRoleForm] = useState({ department_id: '', title: '', description: '' });
  const [toastMsg, setToastMsg] = useState(null);

  useEffect(() => {
    if (toast) {
      setToastMsg(toast);
      const t = setTimeout(() => setToastMsg(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  useEffect(() => {
    if (pageMode === 'view') setEditRow(null);
  }, [pageMode]);

  const canEdit = pageMode === 'edit';
  const allRoles = departmentsWithRoles.flatMap(d => (d.roles || []).map(r => ({ ...r, department_id: d.id, department_title: d.title })));

  const toggleSection = (id) => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));

  const handleDeptAdd = async () => {
    if (!deptForm.title.trim()) { setToastMsg({ message: 'Title required', type: 'error' }); return; }
    const res = await saveDepartmentRow([{ operation: 'ADD', title: deptForm.title.trim(), description: deptForm.description.trim() || null }]);
    if (res.success) setDeptForm({ title: '', description: '' });
  };

  const handleDeptSaveEdit = async () => {
    if (!editRow?.title?.trim()) { setToastMsg({ message: 'Title required', type: 'error' }); return; }
    await saveDepartmentRow([{ operation: 'UPDATE', id: editRow.id, title: editRow.title.trim(), description: editRow.description?.trim() || null }]);
    setEditRow(null);
  };

  const handleDeptDelete = async (row) => {
    if (!window.confirm(`Delete department "${row.title}"? This will fail if employees are assigned to roles in it.`)) return;
    await saveDepartmentRow([{ operation: 'DELETE', id: row.id }]);
  };

  const handleRoleAdd = async () => {
    if (!roleForm.department_id || !roleForm.title.trim()) { setToastMsg({ message: 'Department and title required', type: 'error' }); return; }
    const res = await saveRoleRow([{ operation: 'ADD', department_id: roleForm.department_id, title: roleForm.title.trim(), description: roleForm.description.trim() || null }]);
    if (res.success) setRoleForm({ department_id: '', title: '', description: '' });
  };

  const handleRoleSaveEdit = async () => {
    if (!editRow?.title?.trim()) { setToastMsg({ message: 'Title required', type: 'error' }); return; }
    await saveRoleRow([{ operation: 'UPDATE', id: editRow.id, title: editRow.title.trim(), description: editRow.description?.trim() || null }]);
    setEditRow(null);
  };

  const handleRoleDelete = async (row) => {
    if (!window.confirm(`Delete role "${row.title}"? This will fail if employees hold this role.`)) return;
    await saveRoleRow([{ operation: 'DELETE', id: row.id }]);
  };

  if (loading && !departmentsWithRoles.length) {
    return <div className="p-8 flex justify-center"><Loader /></div>;
  }

  return (
    <div className="flex-1 flex flex-col relative w-full min-h-0 bg-gradient-to-br from-[#FFF8F3] via-white to-[#FFF5EC]">
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-6 mx-auto w-full max-w-6xl space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-primary/90 mb-1">HR database</p>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">HR References</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage departments and roles. Use View to browse; Edit to add, change, or remove rows.
            </p>
          </div>
          <div className="shrink-0 flex flex-col gap-1.5" role="group" aria-label="Page mode">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Mode</span>
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
              <button
                type="button"
                onClick={() => setPageMode('view')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition ${pageMode === 'view' ? 'bg-orange-50 text-orange-primary shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                aria-pressed={pageMode === 'view'}
              >
                <Eye className="h-4 w-4 shrink-0" aria-hidden />
                View
              </button>
              <button
                type="button"
                onClick={() => setPageMode('edit')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition ${pageMode === 'edit' ? 'bg-orange-50 text-orange-primary shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                aria-pressed={pageMode === 'edit'}
              >
                <Pencil className="h-4 w-4 shrink-0" aria-hidden />
                Edit
              </button>
            </div>
          </div>
        </div>

        {/* Departments section */}
        <section className="scroll-mt-4 rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('departments')}
            className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-gray-50/90 transition-colors border-b border-gray-100"
            aria-expanded={openSections.departments}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-primary">
                <Building2 className="h-5 w-5" aria-hidden />
              </span>
              <h2 className="text-base font-semibold text-gray-900 sm:text-lg">Departments</h2>
            </div>
            {openSections.departments ? <ChevronDown className="w-5 h-5 text-gray-500 shrink-0" /> : <ChevronRight className="w-5 h-5 text-gray-500 shrink-0" />}
          </button>
          {openSections.departments && (
            <div className="px-5 pb-5 pt-4 border-t border-gray-100">
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <table className="w-full min-w-[320px] text-sm">
                  <thead className="bg-light-orange/80">
                    <tr>
                      <th className={TH}>Title</th>
                      <th className={TH}>Description</th>
                      {canEdit && <th className={TH_R}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {departmentsWithRoles.length === 0 && (
                      <tr>
                        <td colSpan={canEdit ? 3 : 2} className="px-4 py-8 text-center text-sm text-gray-400">No departments.</td>
                      </tr>
                    )}
                    {departmentsWithRoles.map(dept => {
                      const isEditing = canEdit && editRow?.sectionId === 'departments' && editRow?.id === dept.id;
                      return (
                        <tr key={dept.id} className={TR}>
                          <td className={isEditing ? TD : TD_STRONG}>
                            {isEditing ? (
                              <input className={INPUT} value={editRow.title} onChange={e => setEditRow({ ...editRow, title: e.target.value })} />
                            ) : dept.title}
                          </td>
                          <td className={TD}>
                            {isEditing ? (
                              <input className={INPUT} value={editRow.description || ''} onChange={e => setEditRow({ ...editRow, description: e.target.value })} />
                            ) : (dept.description || <span className="text-gray-400">—</span>)}
                          </td>
                          {canEdit && (
                            <td className={TD_ACT}>
                              {isEditing ? (
                                <span className="inline-flex flex-wrap items-center justify-end gap-2">
                                  <button type="button" className={BTN_EDIT} disabled={saving} onClick={handleDeptSaveEdit}>Save</button>
                                  <button type="button" className={BTN_GHOST} onClick={() => setEditRow(null)}>Cancel</button>
                                </span>
                              ) : (
                                <span className="inline-flex flex-wrap items-center justify-end gap-2">
                                  <button type="button" className={BTN_EDIT} onClick={() => setEditRow({ sectionId: 'departments', ...dept })}>Edit</button>
                                  <button type="button" className={BTN_DANGER} disabled={saving} onClick={() => handleDeptDelete(dept)}>Delete</button>
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {canEdit && (
                      <tr className={TR_ADD}>
                        <td className={TD}>
                          <input className={INPUT} placeholder="New department" value={deptForm.title} disabled={!!editRow} onChange={e => setDeptForm(f => ({ ...f, title: e.target.value }))} />
                        </td>
                        <td className={TD}>
                          <input className={INPUT} placeholder="Description (optional)" value={deptForm.description} disabled={!!editRow} onChange={e => setDeptForm(f => ({ ...f, description: e.target.value }))} />
                        </td>
                        <td className={TD_ACT}>
                          <button type="button" className={BTN_ADD} disabled={!!editRow || saving} onClick={handleDeptAdd}>
                            <Plus size={14} />
                            Add
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* Roles section */}
        <section className="scroll-mt-4 rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('roles')}
            className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-gray-50/90 transition-colors border-b border-gray-100"
            aria-expanded={openSections.roles}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-primary">
                <ShieldCheck className="h-5 w-5" aria-hidden />
              </span>
              <h2 className="text-base font-semibold text-gray-900 sm:text-lg">Roles</h2>
            </div>
            {openSections.roles ? <ChevronDown className="w-5 h-5 text-gray-500 shrink-0" /> : <ChevronRight className="w-5 h-5 text-gray-500 shrink-0" />}
          </button>
          {openSections.roles && (
            <div className="px-5 pb-5 pt-4 border-t border-gray-100">
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <table className="w-full min-w-[320px] text-sm">
                  <thead className="bg-light-orange/80">
                    <tr>
                      <th className={TH}>Department</th>
                      <th className={TH}>Title</th>
                      <th className={TH}>Description</th>
                      {canEdit && <th className={TH_R}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {allRoles.length === 0 && (
                      <tr>
                        <td colSpan={canEdit ? 4 : 3} className="px-4 py-8 text-center text-sm text-gray-400">No roles.</td>
                      </tr>
                    )}
                    {allRoles.map(role => {
                      const isEditing = canEdit && editRow?.sectionId === 'roles' && editRow?.id === role.id;
                      return (
                        <tr key={role.id} className={TR}>
                          <td className={TD}>{role.department_title}</td>
                          <td className={isEditing ? TD : TD_STRONG}>
                            {isEditing ? (
                              <input className={INPUT} value={editRow.title} onChange={e => setEditRow({ ...editRow, title: e.target.value })} />
                            ) : role.title}
                          </td>
                          <td className={TD}>
                            {isEditing ? (
                              <input className={INPUT} value={editRow.description || ''} onChange={e => setEditRow({ ...editRow, description: e.target.value })} />
                            ) : (role.description || <span className="text-gray-400">—</span>)}
                          </td>
                          {canEdit && (
                            <td className={TD_ACT}>
                              {isEditing ? (
                                <span className="inline-flex flex-wrap items-center justify-end gap-2">
                                  <button type="button" className={BTN_EDIT} disabled={saving} onClick={handleRoleSaveEdit}>Save</button>
                                  <button type="button" className={BTN_GHOST} onClick={() => setEditRow(null)}>Cancel</button>
                                </span>
                              ) : (
                                <span className="inline-flex flex-wrap items-center justify-end gap-2">
                                  <button type="button" className={BTN_EDIT} onClick={() => setEditRow({ sectionId: 'roles', ...role })}>Edit</button>
                                  <button type="button" className={BTN_DANGER} disabled={saving} onClick={() => handleRoleDelete(role)}>Delete</button>
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {canEdit && (
                      <tr className={TR_ADD}>
                        <td className={TD}>
                          <select
                            className={INPUT}
                            value={roleForm.department_id}
                            disabled={!!editRow}
                            onChange={e => setRoleForm(f => ({ ...f, department_id: e.target.value }))}
                          >
                            <option value="">-- Department --</option>
                            {departmentsWithRoles.map(d => (
                              <option key={d.id} value={d.id}>{d.title}</option>
                            ))}
                          </select>
                        </td>
                        <td className={TD}>
                          <input className={INPUT} placeholder="Role title" value={roleForm.title} disabled={!!editRow} onChange={e => setRoleForm(f => ({ ...f, title: e.target.value }))} />
                        </td>
                        <td className={TD}>
                          <input className={INPUT} placeholder="Description (optional)" value={roleForm.description} disabled={!!editRow} onChange={e => setRoleForm(f => ({ ...f, description: e.target.value }))} />
                        </td>
                        <td className={TD_ACT}>
                          <button type="button" className={BTN_ADD} disabled={!!editRow || saving} onClick={handleRoleAdd}>
                            <Plus size={14} />
                            Add
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {toastMsg && <Toaster message={toastMsg.message} type={toastMsg.type} />}
      </div>
    </div>
  );
}
